import { create } from 'zustand';
import { Mesh } from 'three';
import JawSegmentation from '@segmentation/three/JawSegmentation.ts';
import JawsSegmentation from '@segmentation/three/JawsSegmentation.ts';
import { threeInteractionStore } from '@modules/three/store/threeInteractionStore';

export enum SegmentationPhase {
  NONE,
  FIX_ROTATION,
  TEETH_SELECTION_LOWER,
  TEETH_SELECTION_UPPER,
  /** Gum repair (bridge scan bays, trim the gum edge) — runs before each jaw's tooth selection. */
  GUM_EDIT_LOWER,
  GUM_EDIT_UPPER,
}

export interface SegmentationState {
  scanId: string | null;
  setScanId: (scanId: string) => void;

  jaws: JawsSegmentation;
  setJaws: (jaws: JawsSegmentation) => void;

  lowerJawSegmentationMesh: JawSegmentation;
  upperJawSegmentationMesh: JawSegmentation;
  setLowerJawSegmentationMesh: (jaw: JawSegmentation) => void;
  setUpperJawSegmentationMesh: (jaw: JawSegmentation) => void;

  segmentationPhase: SegmentationPhase;
  setSegmentationPhase: (segmentationPhase: SegmentationPhase) => void;

  upperJawTeeth: Array<Mesh>;
  lowerJawTeeth: Array<Mesh>;
  setUpperJawTeeth: (teeth: Array<Mesh>) => void;
  setLowerJawTeeth: (teeth: Array<Mesh>) => void;

  update: () => void;

  /** Set after dev snapshot load so UI jumps to contour edit / segment. */
  devReadyJaw: 'lower' | 'upper' | null;
  setDevReadyJaw: (jaw: 'lower' | 'upper' | null) => void;

  processingActive: boolean;
  processingPhase: string;
  processingJaw: 'lower' | 'upper' | null;
  startProcessing: (jaw: 'lower' | 'upper') => void;
  setProcessingPhase: (phase: string) => void;
  stopProcessing: () => void;

  reset: () => void;
}

export const segmentationStore = create<SegmentationState>((set, get) => ({
  scanId: null,
  setScanId: (scanId) => set({ scanId }),

  jaws: null!,
  setJaws: (jaws) => set({ jaws }),

  lowerJawSegmentationMesh: null!,
  upperJawSegmentationMesh: null!,
  setLowerJawSegmentationMesh: (jaw) => set({ lowerJawSegmentationMesh: jaw }),
  setUpperJawSegmentationMesh: (jaw) => set({ upperJawSegmentationMesh: jaw }),

  segmentationPhase: SegmentationPhase.NONE,
  setSegmentationPhase: (segmentationPhase) => {
    set({ segmentationPhase });
    const state = get();
    changePhase(segmentationPhase, state);
  },

  upperJawTeeth: [],
  lowerJawTeeth: [],
  setUpperJawTeeth: (teeth) => set({ upperJawTeeth: teeth }),
  setLowerJawTeeth: (teeth) => set({ lowerJawTeeth: teeth }),
  update: () => {
    get().setJaws(get().jaws);
  },
  devReadyJaw: null,
  setDevReadyJaw: (jaw) => set({ devReadyJaw: jaw }),
  processingActive: false,
  processingPhase: '',
  processingJaw: null,
  startProcessing: (jaw) =>
    set({
      processingActive: true,
      processingJaw: jaw,
      processingPhase: '',
    }),
  setProcessingPhase: (phase) => set({ processingPhase: phase }),
  stopProcessing: () =>
    set({
      processingActive: false,
      processingPhase: '',
      processingJaw: null,
    }),
  reset: () =>
    set(() => ({
      scanId: null,
      jaws: undefined,
      lowerJawSegmentationMesh: undefined,
      upperJawSegmentationMesh: undefined,
      segmentationPhase: SegmentationPhase.NONE,
      upperJawTeeth: [],
      lowerJawTeeth: [],
      devReadyJaw: null,
      processingActive: false,
      processingPhase: '',
      processingJaw: null,
    })),
}));

const changePhase = (segmentationPhase: SegmentationPhase, state: SegmentationState) => {
  const lowerJaw = state.jaws.lowerJaw;
  const upperJaw = state.jaws.upperJaw;
  threeInteractionStore.getState().setPointAdding(true);
  switch (segmentationPhase) {
    case SegmentationPhase.FIX_ROTATION: // TODO
      upperJaw.visible = false;
      upperJaw.layers.disableAll();
      break;
    case SegmentationPhase.NONE:
      upperJaw.visible = true;
      lowerJaw.visible = true;
      threeInteractionStore.getState().setPointAdding(false);
      break;
    case SegmentationPhase.GUM_EDIT_LOWER:
      upperJaw.visible = false;
      upperJaw.layers.disableAll();
      lowerJaw.visible = true;
      lowerJaw.layers.enableAll();
      // Gum edit drives all pointer interaction through its own tool.
      threeInteractionStore.getState().setPointAdding(false);
      break;
    case SegmentationPhase.GUM_EDIT_UPPER:
      lowerJaw.visible = false;
      lowerJaw.layers.disableAll();
      upperJaw.visible = true;
      upperJaw.layers.enableAll();
      threeInteractionStore.getState().setPointAdding(false);
      break;
    case SegmentationPhase.TEETH_SELECTION_LOWER:
      upperJaw.visible = false;
      upperJaw.layers.disableAll();
      lowerJaw.visible = true;
      lowerJaw.layers.enableAll();
      break;
    case SegmentationPhase.TEETH_SELECTION_UPPER:
      lowerJaw.visible = false;
      upperJaw.visible = true;
      lowerJaw.layers.disableAll();
      upperJaw.layers.enableAll();
      break;
  }
};
